import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useGetTiers } from "@/hooks/queries/useGetTiers";
import { useUser } from "@/hooks/useUserProvider";
import { addDays, differenceInDays, format } from "date-fns";
import {
  CheckCircle2Icon,
  ChevronsLeftRightEllipsisIcon,
  CreditCardIcon,
  LandmarkIcon,
  Loader2Icon,
  SparklesIcon,
  UsersIcon,
  XCircleIcon,
  ZapIcon,
} from "lucide-react";
import { useMemo } from "react";
import { BenefitItem } from "./BenefitItem";
import { Button } from "@/components/ui/button";
import { usePaymentModification } from "@/hooks/usePaymentModification";

export const Premium = () => {
  const { user } = useUser();
  const { cancelLoading, changeLoading, updateLoading, handleMethod } =
    usePaymentModification({ subscription: user!.subscription! });

  const somethingLoading = useMemo(
    () => [cancelLoading, changeLoading, updateLoading].some(l => l),
    [cancelLoading, changeLoading, updateLoading],
  );

  const { data: tiers, isLoading, isError, error } = useGetTiers();

  const currentTier = useMemo(
    () => tiers?.find(tier => tier.slug === "plus"),
    [tiers],
  );

  const monthlyPrice = useMemo(
    () => currentTier?.prices.find(price => price.type === "monthly")?.price,
    [currentTier],
  );

  const yearlyPrice = useMemo(
    () => currentTier?.prices.find(price => price.type === "yearly")?.price,
    [currentTier],
  );

  const isYearly =
    differenceInDays(
      user!.subscription!.endDate,
      user!.subscription!.startDate,
    ) > 30;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-md bg-gradient-to-br from-primary/5 to-primary/10 p-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-2">
                <Skeleton className="h-4 w-32 rounded-md" />
                <Skeleton className="h-3 w-40 rounded-md" />
              </div>
              <Skeleton className="h-7 w-24 rounded-full" />
            </div>
            <Separator />
            <div className="space-y-2">
              <Skeleton className="h-9 w-28 rounded-md" />
              <Skeleton className="h-3 w-32 rounded-md" />
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <Skeleton className="h-4 w-28 rounded-md" />
          <div className="grid gap-2 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-10 rounded-md" />
            ))}
          </div>
        </div>
        <Separator />
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Skeleton className="h-9 w-44 rounded-md" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-44 rounded-md" />
            <Skeleton className="h-9 w-32 rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !tiers || monthlyPrice == null || yearlyPrice == null) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Subscription prices not found</AlertTitle>
        <AlertDescription>
          {isError && error instanceof Error
            ? error.message
            : "Failed to load subscription prices."}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md bg-gradient-to-br from-primary/5 to-primary/10 p-4">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">Active Subscription</h3>
              <p className="text-xs text-muted-foreground">
                {`Next billing on ${format(
                  addDays(user!.subscription!.endDate, 1),
                  "MMMM dd, yyyy",
                )}`}
              </p>
            </div>
            <div className="flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1">
              <CheckCircle2Icon className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium">Premium</span>
            </div>
          </div>
          <Separator />
          <div className="flex flex-col gap-2">
            <div className="space-y-1">
              <div className="flex items-baseline gap-1">
                <>
                  <span className="text-3xl font-bold">{`€${(isYearly ? yearlyPrice / 12 : monthlyPrice).toFixed(2)}`}</span>
                  <span className="text-sm text-muted-foreground">/ month</span>
                </>
              </div>
              {isYearly && (
                <p className="text-xs font-medium text-primary">
                  Billed annually: €{yearlyPrice.toFixed(2)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <h3 className="text-sm font-medium">Premium Benefits</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <BenefitItem
            icon={<SparklesIcon className="h-4 w-4 text-primary" />}
            title="Premium Widgets"
          />
          <BenefitItem
            icon={<ZapIcon className="h-4 w-4 text-primary" />}
            title="Advanced Features"
          />
          <BenefitItem
            icon={<UsersIcon className="h-4 w-4 text-primary" />}
            title="More Integrations"
          />
          <BenefitItem
            icon={<CreditCardIcon className="h-4 w-4 text-primary" />}
            title="Priority Support"
          />
        </div>
      </div>
      <Separator />
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={handleMethod("cancel")}
            disabled={somethingLoading}
          >
            {cancelLoading ? (
              <>
                <Loader2Icon className="animate-spin" />
                Submitting Request
              </>
            ) : (
              <>
                <XCircleIcon />
                Cancel Subscription
              </>
            )}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleMethod("manage")}
            disabled={somethingLoading}
          >
            {updateLoading ? (
              <>
                <Loader2Icon className="animate-spin" />
                Submitting Request
              </>
            ) : (
              <>
                <LandmarkIcon />
                Manage Subscription
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleMethod("change")}
            disabled={somethingLoading}
          >
            {changeLoading ? (
              <>
                <Loader2Icon className="animate-spin" />
                Submitting Request
              </>
            ) : (
              <>
                <ChevronsLeftRightEllipsisIcon />
                Change Plan
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
